/* Fluent's button. Standard: a control fill one shade above the layer, a
   subtle stroke on three sides and a firm one on the bottom. Accent: filled,
   with the ink inverted. Subtle: nothing until pointed at. Danger: critical
   ink, filled only on hover. All 32px, all 4px-cornered. */

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  white-space: nowrap;
  border-radius: var(--radius-md);
  border: var(--border-hairline) solid transparent;
  font-size: var(--text-sm);
  font-weight: 400;
  transition:
    background-color var(--duration-fast) var(--ease-out),
    border-color var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}

.button:active:not(:disabled) {
  color: var(--ink-muted);
}

.button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

/* Busy is not disabled: the control is working, so it keeps its colour and
   takes a progress cursor rather than fading out like something switched off. */
.button[aria-busy="true"] {
  cursor: progress;
  opacity: 1;
}

/* --- size ------------------------------------------------------------- */

.button[data-size="sm"] {
  height: var(--control-h-sm);
  padding-inline: var(--space-2);
  font-size: var(--text-xs);
}

.button[data-size="md"] {
  height: var(--control-h-md);
  padding-inline: var(--space-3);
}

.button[data-size="lg"] {
  height: var(--control-h-lg);
  padding-inline: var(--space-5);
  font-size: var(--text-base);
}

.button[data-icon-only] {
  padding-inline: 0;
  aspect-ratio: 1;
}

.button[data-icon-only][data-size="sm"] {
  width: var(--control-h-sm);
}

.button[data-icon-only][data-size="md"] {
  width: var(--control-h-md);
}

.button[data-icon-only][data-size="lg"] {
  width: var(--control-h-lg);
}

/* A thumb needs 44px and a mouse does not. Every button, not only the
   icon-only ones: a 24px "Log food" beside a 44px refresh button was both
   too small to hit and out of line with its neighbour. `height: auto` because
   the size rules set a fixed height, which a min-height cannot lift. */
@media (pointer: coarse) {
  .button[data-size] {
    height: auto;
    min-height: var(--control-h-touch);
  }

  .button[data-icon-only] {
    min-width: var(--control-h-touch);
  }
}

/* --- intent ----------------------------------------------------------- */

/* The stroke is what carries "this is a control" against a layer one shade
   away; --line-control is the token measured to 3:1 for that job. */
.button[data-intent="default"] {
  background: var(--control-fill);
  border-color: var(--line-control);
  box-shadow: var(--part-edge);
  color: var(--ink);
}

.button[data-intent="default"]:hover:not(:disabled) {
  background: var(--control-fill-hover);
}

.button[data-intent="default"]:active:not(:disabled) {
  background: var(--control-fill-active);
  box-shadow: none;
}

/* The accent button: filled, and the one loud control on any panel. */
.button[data-intent="primary"] {
  background: var(--accent);
  border-color: var(--accent);
  box-shadow: inset 0 -1px 0 rgb(0 0 0 / 20%);
  color: var(--accent-ink);
  font-weight: 600;
}

.button[data-intent="primary"]:hover:not(:disabled) {
  background: var(--accent-hover);
  border-color: var(--accent-hover);
}

.button[data-intent="primary"]:active:not(:disabled) {
  box-shadow: none;
  color: color-mix(in srgb, var(--accent-ink) 80%, transparent);
}

/* Subtle: for actions beside content — a row's rename, a widget's refresh. */
.button[data-intent="quiet"] {
  color: var(--ink-muted);
}

.button[data-intent="quiet"]:hover:not(:disabled) {
  background: var(--surface-hover);
  color: var(--ink);
}

.button[data-intent="quiet"]:active:not(:disabled) {
  background: var(--surface-active);
}

/* Destructive actions are outlined rather than filled. */
.button[data-intent="danger"] {
  color: var(--danger);
  border-color: color-mix(in srgb, var(--danger) 45%, transparent);
}

.button[data-intent="danger"]:hover:not(:disabled) {
  background: var(--danger-soft);
  border-color: var(--danger);
}

.full {
  width: 100%;
}

.icon {
  width: 1rem;
  height: 1rem;
  flex: none;
}

.button[data-size="lg"] .icon {
  width: 1.125rem;
  height: 1.125rem;
}

.label {
  overflow: hidden;
  text-overflow: ellipsis;
}
